// src/components/Recommendation.tsx
// Fixed version with treatment2 visible on all screen sizes
import useBackPocketRecommendations from "@hooks/useBackPocketRecommendations";
import { hasBackPocketPrescription, getBackPocketPrescriptionIds } from '@constants/antibiotics';

import styled from "styled-components";
import useGetRecommendation from "@hooks/useGetRecommendation";
import mediaQuery from "@utils/mediaQuery";
import Treatments from "./Treatments";
import LineWithText from "./TreatmentDivider";

import EditIcon from "@assets/icons/eidt-button.svg";
import { Button } from "@te-whatu-ora/anatomic";
import { useEffect, useMemo, useState } from "react";
import RichText from "./RichText";
import { formatDate } from "@utils/date";
import {
  getIdApprovalAntimicrobials,
  getLastReviewedDate,
} from "@utils/recommendation";
import Banner from "./Banner";
import { Link } from "react-router-dom";
import TextButton from "./TextButton";
import RecommendationCopy from "./RecommendationCopy";
import ROUTES from "@constants/routes";
import FeedbackForm from "./FeedbackForm";

import AllergyBanner from "./AllergyBanner";
import ContextDebugger from "./ContextDebugger";
import { useContextualAllergyMessages } from "@utils/allergyMessageUtil";

interface Props {
  id: string;
  onEdit: () => void;
  onNewStart: () => void;
  onRecommendationAvailable: () => void;
  events: string[];
  context?: any;
}

const Container = styled.div`
  padding: 80px 16px;
  background: var(--bg-recommendation);
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  gap: 22px;
  margin: 0 -16px;
  box-sizing: border-box;
  margin-bottom: -88px;

  ${mediaQuery("tablet")`
  margin: 0;
  width: 100vw;
  margin-left: -100px;
  margin-bottom: -88px;
  padding: 80px 16px 80px 16px;
  align-items: center;
  `}

  @media screen and (min-width: 1320px) {
    margin-left: calc((1320px - 100vw) / 2 - 100px);
  }
`;

const ContentWrapper = styled.div`
  max-width: 1320px;
`;

const TreatmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${mediaQuery("desktop")`
  flex-direction: row;
  gap: 48px;
`}
`;

const TreatmentWrapper = styled.div`
  padding: 24px 16px;
  background: var(--bg-treatment);
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  gap: 22px;
  margin: 0 -16px;
  margin-top: 24px;

  ${mediaQuery("tablet")`
  margin: 0;
  margin-top: 24px;
  border-radius: 12px;
  `}
`;

const TreatmentLengthText = styled.div`
  width: 70%;
  font-size: var(--font-size-5xl);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-3xl);
  margin-bottom: 20px;

  ${mediaQuery("tablet")`
  font-size: var(--font-size-6xl);
  line-height: var(--line-height-5xl);
  width: 100%;
  `}
`;

const ConditionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-lg);

  ${mediaQuery("tablet")`
  width: 100%;
  font-size: var(--font-size-5xl);
  line-height: var(--line-height-3xl);
  `}

  ${mediaQuery("desktop")`
  width: 66%;
  `}
`;

const EventsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;

  ${mediaQuery("tablet")`
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 24px;
  `}
`;

const EventsText = styled.div`
  flex: 2;
  color: var(--text-secondary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-xl);
  margin-bottom: 8px;

  ${mediaQuery("tablet")`
  margin-bottom: 0;
  `}
`;

const ConditionText = styled.div`
  display: inline-flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

const PrescriptionSection = styled.div`
  flex: 2.5;
  align-items: center;
  flex-direction: column;
  position: relative;
`;

export const NoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  border-radius: 12px;
  background: var(--bg-card);
  box-sizing: border-box;
  padding: 16px;
`;

const ButtonWrapper = styled.div`
  margin-top: 72px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 16px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  color: var(--text-link);

  span {
    display: flex;
  }

  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-3xl);
  text-decoration-line: underline;
  gap: 16px;
  cursor: pointer;
`;

const LastReviewedAt = styled.div`
  flex: 1;
  text-align: right;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-md);
  letter-spacing: var(--letter-spacing-wide);
`;

const ConditionContainer = styled.div`
  display: flex;
  gap: 48px;
  flex-direction: column;

  ${mediaQuery("tablet")`
  flex-direction: row;
  `}
`;

const CopyFABContainer = styled.div`
  display: none;
  
  ${mediaQuery("tablet")`
    display: block;
    position: absolute;
    bottom: -24px;
    right: 16px;
    z-index: 10;
  `}
`;

const CopyFAB = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 2px solid var(--interactive-secondary);
  color: var(--interactive-secondary);
  font-size: var(--font-size-5xl);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 85, 140, 0.2);
  transition: all 0.2s ease;

  &:hover {
    background: var(--interactive-secondary);
    color: var(--text-inverse);
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 85, 140, 0.3);
  }

  &:active {
    transform: scale(1.05);
  }

  &::after {
    content: 'Copy recommendation';
    position: absolute;
    right: 100%;
    margin-right: 12px;
    background: #333;
    color: var(--text-inverse);
    padding: 6px 12px;
    border-radius: 6px;
    font-size: var(--font-size-md);
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const CopyFeedback = styled.div<{ show: boolean }>`
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: #2e7d32;
  color: var(--text-inverse);
  padding: 12px 24px;
  border-radius: 8px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

// Back-pocket prescription styled components - matching RecommendationCopy exactly
const BackPocketContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.4);
  box-sizing: border-box;
  padding: 24px;
  margin-top: 16px;
`;

const BackPocketTitle = styled.p`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-md);
  letter-spacing: var(--letter-spacing-wide);
  margin-bottom: 16px;
  color: var(--text-primary);
  margin-top: 0;

  ${mediaQuery("tablet")`
    font-size: var(--font-size-xl);
    line-height: var(--line-height-xl);
  `}
`;

const TreatmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TreatmentItem = styled.li`
  font-size: var(--font-size-base);
  line-height: var(--line-height-md);
  letter-spacing: var(--letter-spacing-wide);
  color: var(--text-primary);
  margin-bottom: 4px;
  padding-left: 20px;
  position: relative;

  ${mediaQuery("tablet")`
    font-size: var(--font-size-xl);
    line-height: var(--line-height-xl);
  `}

  &:before {
    content: "•";
    position: absolute;
    left: 0;
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
  }

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    font-weight: var(--font-weight-bold);
  }
`;

const OrDivider = styled.span`
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 4px;
`;

const Recommendation = ({
  id,
  onEdit,
  onNewStart,
  onRecommendationAvailable,
  events,
  context,
}: Props) => {
  const { recommendation, errorMessage } = useGetRecommendation(id);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  
  const recommendationId = recommendation?._id ?? null;
  const { 
    backPocketRecommendations, 
    isLoading: isLoadingBackPocket 
  } = useBackPocketRecommendations(recommendationId);
  
  const { messages, hasMessages, allergyCode } = useContextualAllergyMessages(events, recommendation);
  
  useEffect(() => {
    if (recommendation || errorMessage) {
      onRecommendationAvailable();
    }
  }, [recommendation, errorMessage, onRecommendationAvailable]);

  const {
    treatment1,
    treatment2,
    treatmentNotes1,
    treatmentNotes2,
    copy,
    treatmentLength,
    displayTitle,
    condition,
    isAdult,
    requiresApproval,
  } = recommendation || {};

  const showSecondTreatment =
    treatment2 && treatment2?.length > 0 && treatment2?.[0] != null;
  const hasTreatments = !!treatment1;

  const recommendationCopy = copy ?? [];
  const conditionCopy = condition?.content ?? [];
  const recommendationContent = [...recommendationCopy, ...conditionCopy];

  const idApprovals =
    recommendation && getIdApprovalAntimicrobials(recommendation);

  const localizedEvents = events.map(e => e.replace(/\bPediatric\b/g, 'Paediatric'));
  const eventBreadcrumb = localizedEvents.join(" / ");

  const lastReviewedAt = useMemo(() => {
    if (recommendation) {
      return getLastReviewedDate(recommendation);
    }
  }, [recommendation]);

  const handleFeedbackClick = () => {
    setShowFeedbackForm(true);
  };

  const generateCopyText = () => {
    const getFrequencyLabel = (frequency: number, formTitle?: string): string => {
      const form = formTitle?.toLowerCase().trim() || "";
      const isInjectable = form.includes("injection") || form.includes("infusion");

      if (!frequency || frequency <= 0) return "Single dose";
      if (frequency === 1.52) return "Once weekly";
      if (frequency === 3.7) return "every 3 days";

      switch (frequency) {
        case 1:
          return isInjectable ? "every 24 hours" : "once daily";
        case 2:
          return isInjectable ? "every 12 hours" : "twice daily";
        case 3:
          return isInjectable ? "every 8 hours" : "three times daily";
        case 4:
          return isInjectable ? "every 6 hours" : "four times daily";
        case 6:
          return isInjectable ? "every 4 hours" : "six times daily";
        default:
          return `${frequency}× daily`;
      }
    };

    const pathway = localizedEvents.join(" | ");
    let text = `ANTIMICROBIAL RECOMMENDATION\n`;
    text += `Generated from Te Whata Kura | National Antimicrobial Guidelines\n`;
    text += `on: ${new Date().toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' })}\n\n`;
    text += `Condition: ${displayTitle}\n`;
    text += `Clinical Pathway: ${pathway}\n`;
    
    if (treatmentLength) {
      text += `Total treatment duration: ${treatmentLength} days\n\n`;
    }
    
    text += `TREATMENT:\n`;
    
    if (treatment1 && treatment1.length > 0) {
      treatment1.forEach((item: any, index: number) => {
        const parts = [];
        const treatment = item.treatment || item;
        
        const antimicrobialName = treatment.titleDisplay || 
                                  treatment.antimicrobial?.title || 
                                  'Unknown antimicrobial';
        parts.push(antimicrobialName);
        
        const dose = treatment.dose;
        const unit = treatment.antimicrobialUnit?.doseUnit?.shorthand;
        const hasDose = !!(dose && unit);
        if (hasDose) {
          parts.push(`${dose}${unit}`);
        }

        const formTitle = treatment.antimicrobialUnit?.form?.title;
        if (formTitle) {
          parts.push(formTitle);
        }

        const frequency = treatment.frequency;
        const hasFreq = !!(frequency && Number(frequency) >= 1);
        const hasBasicData = hasDose || hasFreq;
        const complex = treatment.complex;

        if (complex && !hasBasicData) {
          parts.push('Refer to complex dosing table or calculator');
        } else if (hasFreq) {
          parts.push(getFrequencyLabel(frequency, formTitle));
        } else {
          parts.push('Single dose');
        }

        const duration = treatment.duration;
        const hasDuration = duration !== null && duration !== undefined && Number(duration) > 0;
        if (hasDuration && hasFreq) {
          parts.push(`for ${duration} days`);
        }

        if (treatment.food) {
          parts.push('with food');
        }

        text += `- ${parts.join(' ')}`;

        if (index < treatment1.length - 1) {
          text += ` and\n`;
        } else {
          text += `\n`;
        }
      });
    }

    if (showSecondTreatment) {
      text += `\nthen switch to\n\n`;
      treatment2?.forEach((item: any, index: number) => {
        const parts = [];
        const treatment = item.treatment || item;

        const antimicrobialName = treatment.titleDisplay ||
                                  treatment.antimicrobial?.title ||
                                  'Unknown antimicrobial';
        parts.push(antimicrobialName);

        const dose = treatment.dose;
        const unit = treatment.antimicrobialUnit?.doseUnit?.shorthand;
        const hasDose = !!(dose && unit);
        if (hasDose) {
          parts.push(`${dose}${unit}`);
        }

        const formTitle = treatment.antimicrobialUnit?.form?.title;
        if (formTitle) {
          parts.push(formTitle);
        }

        const frequency = treatment.frequency;
        const hasFreq = !!(frequency && Number(frequency) >= 1);
        const hasBasicData = hasDose || hasFreq;
        const complex = treatment.complex;

        if (complex && !hasBasicData) {
          parts.push('Refer to complex dosing table or calculator');
        } else if (hasFreq) {
          parts.push(getFrequencyLabel(frequency, formTitle));
        } else {
          parts.push('Single dose');
        }

        const duration = treatment.duration;
        const hasDuration = duration !== null && duration !== undefined && Number(duration) > 0;
        if (hasDuration && hasFreq) {
          parts.push(`for ${duration} days`);
        }

        if (treatment.food) {
          parts.push('with food');
        }
        
        text += `- ${parts.join(' ')}`;
        
        if (index < treatment2.length - 1) {
          text += ` and\n`;
        } else {
          text += `\n`;
        }
      });
    }
 
    return text;
  };

  const handleCopyRecommendation = async () => {
    if (!recommendation) return;
        
    const copyText = generateCopyText();
    
    try {
      await navigator.clipboard.writeText(copyText);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = copyText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 3000);
      } catch (fallbackErr) {
        alert('Unable to copy to clipboard. Please try selecting and copying the text manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const getFrequencyLabel = (frequency: number): string => {
    if (frequency === 0 || !frequency) return 'Single dose';
    if (frequency === 1) return 'once daily';
    if (frequency === 2) return 'twice daily';
    if (frequency === 3) return 'three times daily';
    if (frequency === 4) return 'four times daily';
    return `${frequency}× daily`;
  };

  const formatTreatmentLine = (treatment: any): string => {
    const parts = [];
    
    const name = treatment.titleDisplay || treatment.antimicrobial?.title || 'Unknown';
    
    const dose = treatment.dose;
    const unit = treatment.antimicrobialUnit?.doseUnit?.shorthand;
    if (dose && unit) {
      parts.push(`${dose}${unit}`);
    }
    
    const frequency = treatment.frequency;
    if (frequency !== undefined && frequency !== null) {
      parts.push(getFrequencyLabel(frequency));
    }
    
    const duration = treatment.duration;
    const isSingleDose = frequency === 0 || !frequency || frequency < 1;
    if (duration && !isSingleDose) {
      parts.push(`for ${duration} days`);
    }
    
    return parts.join(' ');
  };

  const renderBackPocketPrescription = () => {
    if (!backPocketRecommendations || backPocketRecommendations.length === 0) {
      return null;
    }

    // Flatten all treatments from all recommendations
    const allTreatments = backPocketRecommendations.flatMap((backPocketRec, recIndex) => {
      const treatments = backPocketRec.treatment1 || [];
      return treatments.map((item: any, treatmentIndex: number) => {
        const treatment = item.treatment || item;
        const name = treatment.titleDisplay || treatment.antimicrobial?.title || 'Unknown';
        const details = formatTreatmentLine(treatment);
        
        return {
          key: `${recIndex}-${treatmentIndex}`,
          name,
          details
        };
      });
    });

    return (
      <BackPocketContainer>
        <BackPocketTitle>
          Backpocket prescription details if required
        </BackPocketTitle>
        
        <TreatmentList>
          {allTreatments.map((treatment, index) => (
            <TreatmentItem key={treatment.key}>
              <strong>{treatment.name}</strong> {treatment.details}
              {index < allTreatments.length - 1 && <OrDivider>OR</OrDivider>}
            </TreatmentItem>
          ))}
        </TreatmentList>
      </BackPocketContainer>
    );
  };

  return (
    <Container>
      <CopyFeedback show={showCopySuccess}>
        ✓ Recommendation copied to clipboard
      </CopyFeedback>

      <ContextDebugger events={events} label="Recommendation Events" />
      
      {errorMessage && <div>{errorMessage}</div>}
      {recommendation && (
        <ContentWrapper>
          {treatmentLength && (
            <TreatmentLengthText>
              {treatmentLength} day treatment recommended
            </TreatmentLengthText>
          )}
          <ConditionContainer>
            <ConditionWrapper>
              <ConditionText>
                {displayTitle}
                <EditButton onClick={onEdit}>
                  Edit{/**/}
                  <span>
                    <EditIcon />
                  </span>
                </EditButton>
              </ConditionText>
              <EventsContainer>
                <EventsText>{eventBreadcrumb}</EventsText>
                {lastReviewedAt && (
                  <LastReviewedAt>
                    Last reviewed{" "}
                    {formatDate(new Date(lastReviewedAt), "DAY_MONTH_YEAR")}
                  </LastReviewedAt>
                )}
              </EventsContainer>
            </ConditionWrapper>
          </ConditionContainer>
          {hasTreatments ? (
            <>
              <TreatmentContainer>
                <PrescriptionSection>
                  <AllergyBanner events={events} recommendation={recommendation} />
                  
                  {requiresApproval && (
                    <Banner>
                      Discuss condition management with ID/Micro.{" "}
                      <Link
                        to={ROUTES.expert.to}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Find an expert
                      </Link>
                    </Banner>
                  )}
                  
                  <TreatmentWrapper>
                    <Treatments treatments={treatment1} isAdult={!!isAdult} />
                  </TreatmentWrapper>
                  {treatmentNotes1 && <RichText copy={treatmentNotes1} />}
                  
                  {showSecondTreatment && (
                    <>
                      <LineWithText />
                      <TreatmentWrapper>
                        <Treatments treatments={treatment2} isAdult={!!isAdult} />
                      </TreatmentWrapper>
                      {treatmentNotes2 && <RichText copy={treatmentNotes2} />}
                    </>
                  )}
                  
                  {/* Copy FAB - only visible on tablet+ */}
                  <CopyFABContainer>
                    <CopyFAB onClick={handleCopyRecommendation} aria-label="Copy recommendation to clipboard">
                      📋
                    </CopyFAB>
                  </CopyFABContainer>
                </PrescriptionSection>
                {recommendationContent && recommendationContent.length > 0 && (
                  <RecommendationCopy copy={recommendationContent} />
                )}
              </TreatmentContainer>
              
              <ButtonWrapper>
                <Button onClick={onNewStart}>Start new search</Button>
                <TextButton onClick={handleFeedbackClick}>
                  Give feedback
                </TextButton>
              </ButtonWrapper>
            </>
          ) : (
            <>
              <NoteContainer>
                <AllergyBanner events={events} recommendation={recommendation} />
                
                {requiresApproval && (
                  <Banner>
                    Discuss condition management with ID/Micro.{" "}
                    <Link
                      to={ROUTES.expert.to}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Find an expert
                    </Link>
                  </Banner>
                )}
                {recommendationContent && recommendationContent.length > 0 && (
                  <RecommendationCopy copy={recommendationContent} />
                )}
              </NoteContainer>
              
              {renderBackPocketPrescription()}
              
              <ButtonWrapper>
                <Button onClick={onNewStart}>Start new search</Button>
                <TextButton onClick={handleFeedbackClick}>
                  Give feedback
                </TextButton>
              </ButtonWrapper>
            </>
          )}
        </ContentWrapper>
      )}

      <FeedbackForm
        treatment={displayTitle || ""}
        recommendationName={localizedEvents.join(" | ") || ""}
        recommendationID={id}
        visible={showFeedbackForm}
        onVisibleChange={setShowFeedbackForm}
      />
    </Container>
  );
};

export default Recommendation;